import { ComponentFactoryResolver, Type } from '@angular/core';
import { Logger } from 'app/shared/logger';
import { environment } from 'environments/environment';

export interface ComponentMetadata<TComponent = any> {
    type: string;
    componentType: Type<TComponent>;
    supported: boolean;
}

export abstract class ResolverBase<TComponent extends any, TComponentMetadata extends ComponentMetadata<TComponent>> {
    private componentsByType: Map<string, Type<TComponent>>;
    private metadataByType: Map<string, TComponentMetadata>;

    constructor(private logger: Logger, private componentFactoryResolver: ComponentFactoryResolver, componentMetadatas: TComponentMetadata[]) {

        if (!environment.production) {
            let metadataKeys = componentMetadatas.map(x => x.type).slice().sort();
            let results = [];
            for (let i = 0; i < metadataKeys.length - 1; i++) {
                if (metadataKeys[i + 1] === metadataKeys[i]) {
                    results.push(metadataKeys[i]);
                }
            }

            if (results.length > 0) {
                throw new Error(`Duplicate component metadata keys while loading resolver (copypasta ლ(ಠ益ಠლ)): [${results.map(x => '\'' + x + '\'').join(', ')}]`);
            }
        }

        this.componentsByType = new Map(componentMetadatas.map(x => [x.type, x.componentType] as [string, Type<TComponent>]));
        this.metadataByType = new Map(componentMetadatas.map(x => [x.type, x] as [string, TComponentMetadata]));

        this.logger.info(`These are the available layouts: ${this.getSupportedComponentKeys().join(', ')}`);
    }

    protected abstract getDefaultComponent(): Type<TComponent>;
    protected abstract getDefaultComponentMetadata(): TComponentMetadata;

    public resolveComponent(type: string) {
        this.logger.info(`Resolving component ${type}`);
        let componentType = this.componentsByType.get(type);

        if (!componentType) {
            this.logger.error(`A component of type ${type} was requested but there was no supported component for it. Returning default component.`);
            componentType = this.getDefaultComponent();
        }

        return this.componentFactoryResolver.resolveComponentFactory(componentType);
    }

    public resolveComponentMetadata(type: string) {
        this.logger.info(`Resolving component metadata ${type}`);
        let layout = this.metadataByType.get(type);

        if (!layout) {
            this.logger.error(`A metadata of type ${type} was requested but there was no supported metadata for it. Returning default metadata.`);
            layout = this.getDefaultComponentMetadata();
        }

        return layout;
    }

    public getSupportedComponentKeys(): string[] {
        return Array.from(this.metadataByType.values()).filter(x => x.supported).map(x => x.type);
    }
}


// WEBPACK FOOTER //
// ./src/app/views/presentations/shared/resolverBase.ts